import { createAsyncThunk } from '@reduxjs/toolkit';
import { ResponseErrorWithHandled, ResponseSuccess } from '../../shared/types';
import {
  emailAccountTagsAssign,
  emailAccountTagsBulkAssign,
  emailAccountTagsBulkUnassign,
  emailAccountTagsUnassign,
  getEmailAccountsInWarmup,
  getEmailAccountTags,
  getEmailAccountWarmupDeliverabilityReport,
  getEmailAccountWarmupList,
  getEmailAccountWarmupSentReceivedReport,
  getEmailAccountWarmupSettings,
  getTagsInEmailWarmup,
  importEmailAccountCsv,
  saveEmailAccountWarmupSettings,
  syncSaleshandyEmailAccountWithApiKey,
  turnOnOffEmailAccountWarmup,
  saveBulkEmailAccountWarmupSettings,
  updateAllEmailAccountWarmupSettings,
  enableDisableDeleteSelectedEmailAccountWarmup,
  enableDisableDeleteBulkEmailAccountWarmup,
  getEmailAccountsBulkActionStatus,
  refreshEmailAccount,
  getEmailAccountWarmupReportStats,
  getEmailAccountWarmupReportDeliverabilityScore,
  getEmailAccountWarmupReportESPBreakdown,
  getEmailAccountWarmupReportGraph,
  getEmailAccountWarmupReportExport,
  emailAccountsWarmupExport,
  sendTableEmailWarmupSettingsStrategyEdit,
  // getEmailAccountWarmupBulkStatus,
  getEmailAccountWarmupBulkRunningStoppedStatus,
  saveEmailWarmupErrorMessage,
  getEmailAccountActivitySummary,
  getEmailAccountActivityEvents,
  getEmailAccountWarmupActivityHistory,
} from './helpers/email-warmup.api';
import {
  ApiAction,
  BulkAllEmailAccountWarmupSettingsPayload,
  BulkEmailAccountWarmupSettingsPayload,
  EmailAccountBulkActionFilters,
  EmailAccountId,
  EmailAccountTagsAssignPayload,
  EmailAccountTagsBulkAssignPayload,
  EmailAccountTagsBulkUnassignPayload,
  EmailAccountTagsUnassignPayload,
  EmailAccountWarmupFilters,
  EmailAccountWarmupSettingsPayload,
  EmailAccountsWarmupExportPayload,
  EmailWarmupDeliverabilityPayload,
  EmailWarmupErrorMessagePayload,
  EmailWarmupReportFiltersPayload,
  EmailActivitySummaryPayload,
  EmailActivityEventsPayload,
  WarmupActivityHistoryPayload,
} from './types';

export const getEmailAccountsInWarmupRequest = createAsyncThunk<
  ResponseSuccess,
  EmailAccountWarmupFilters,
  { rejectValue: ResponseErrorWithHandled }
>(
  'emailWarmup/getEmailAccountsInWarmup',
  async (args: EmailAccountWarmupFilters, thunkAPI) => {
    try {
      return await getEmailAccountsInWarmup(args);
    } catch (e) {
      return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
    }
  },
);

export const getEmailAccountWarmupDeliverabilityReportRequest = createAsyncThunk<
  ResponseSuccess,
  EmailWarmupDeliverabilityPayload,
  { rejectValue: ResponseErrorWithHandled }
>(
  'emailWarmup/getEmailAccountWarmupDeliverabilityReport',
  async (args, thunkAPI) => {
    try {
      return await getEmailAccountWarmupDeliverabilityReport(args);
    } catch (e) {
      return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
    }
  },
);

export const getEmailAccountWarmupListRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('emailWarmup/getEmailAccountWarmupList', async (_args, thunkAPI) => {
  try {
    return await getEmailAccountWarmupList();
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const getEmailAccountWarmupSentReceivedReportRequest = createAsyncThunk<
  ResponseSuccess,
  EmailAccountId,
  { rejectValue: ResponseErrorWithHandled }
>(
  'emailWarmup/getEmailAccountWarmupSentReceivedReport',
  async (args, thunkAPI) => {
    try {
      return await getEmailAccountWarmupSentReceivedReport(args);
    } catch (e) {
      return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
    }
  },
);

export const getEmailAccountWarmupSettingsRequest = createAsyncThunk<
  ResponseSuccess,
  EmailAccountId,
  { rejectValue: ResponseErrorWithHandled }
>('emailWarmup/getEmailAccountWarmupSettings', async (args, thunkAPI) => {
  try {
    return await getEmailAccountWarmupSettings(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const saveEmailAccountWarmupSettingsRequest = createAsyncThunk<
  ResponseSuccess,
  EmailAccountWarmupSettingsPayload,
  { rejectValue: ResponseErrorWithHandled }
>('emailWarmup/saveEmailAccountWarmupSettings', async (args, thunkAPI) => {
  try {
    return await saveEmailAccountWarmupSettings(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const sendTableEmailWarmupSettingsStrategyEditRequest = createAsyncThunk<
  ResponseSuccess,
  EmailAccountWarmupSettingsPayload,
  { rejectValue: ResponseErrorWithHandled }
>(
  'emailWarmup/sendTableEmailWarmupSettingsStrategyEdit',
  async (args, thunkAPI) => {
    try {
      return await sendTableEmailWarmupSettingsStrategyEdit(args);
    } catch (e) {
      return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
    }
  },
);

export const saveBulkEmailAccountWarmupSettingsRequest = createAsyncThunk<
  ResponseSuccess,
  BulkEmailAccountWarmupSettingsPayload,
  { rejectValue: ResponseErrorWithHandled }
>('emailWarmup/saveBulkEmailAccountWarmupSettings', async (args, thunkAPI) => {
  try {
    return await saveBulkEmailAccountWarmupSettings(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const updateAllEmailAccountWarmupSettingsRequest = createAsyncThunk<
  ResponseSuccess,
  BulkAllEmailAccountWarmupSettingsPayload,
  { rejectValue: ResponseErrorWithHandled }
>('emailWarmup/updateAllEmailAccountWarmupSettings', async (args, thunkAPI) => {
  try {
    return await updateAllEmailAccountWarmupSettings(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const turnOnOffEmailAccountWarmupRequest = createAsyncThunk<
  ResponseSuccess,
  EmailAccountId,
  { rejectValue: ResponseErrorWithHandled }
>('emailWarmup/turnOnOffEmailAccountWarmup', async (args, thunkAPI) => {
  try {
    return await turnOnOffEmailAccountWarmup(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const importEmailAccountCsvRequest = createAsyncThunk<
  ResponseSuccess,
  { file: File },
  { rejectValue: ResponseErrorWithHandled }
>('emailAccount/import', async ({ file }, thunkAPI) => {
  try {
    return await importEmailAccountCsv(file);
  } catch (err) {
    return thunkAPI.rejectWithValue(err as ResponseErrorWithHandled);
  }
});

export const syncSaleshandyEmailAccountWithApiKeyRequest = createAsyncThunk<
  ResponseSuccess,
  { apiKey: string },
  { rejectValue: ResponseErrorWithHandled }
>('saleshandyEmailAccountWithApiKey', async ({ apiKey }, thunkAPI) => {
  try {
    return await syncSaleshandyEmailAccountWithApiKey(apiKey);
  } catch (err) {
    return thunkAPI.rejectWithValue(err as ResponseErrorWithHandled);
  }
});
export const getTagsEmailWarmupRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('email-account/getTagsRequest', async (_, thunkAPI) => {
  try {
    return await getTagsInEmailWarmup();
  } catch (err) {
    return thunkAPI.rejectWithValue(err as ResponseErrorWithHandled);
  }
});

export const getEmailAccountsTagsRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('emailWarmup/getEmailAccountsTags', async (_, thunkAPI) => {
  try {
    return await getEmailAccountTags();
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const emailAccountTagsAssignRequest = createAsyncThunk<
  ResponseSuccess,
  EmailAccountTagsAssignPayload,
  { rejectValue: ResponseErrorWithHandled }
>('emailWarmup/emailAccountTagsAssign', async (args, thunkAPI) => {
  try {
    return await emailAccountTagsAssign(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const emailAccountTagsBulkAssignRequest = createAsyncThunk<
  ResponseSuccess,
  EmailAccountTagsBulkAssignPayload,
  { rejectValue: ResponseErrorWithHandled }
>('emailWarmup/emailAccountTagsBulkAssign', async (args, thunkAPI) => {
  try {
    return await emailAccountTagsBulkAssign(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const emailAccountTagsUnassignRequest = createAsyncThunk<
  ResponseSuccess,
  EmailAccountTagsUnassignPayload,
  { rejectValue: ResponseErrorWithHandled }
>('emailWarmup/emailAccountTagsUnassign', async (args, thunkAPI) => {
  try {
    return await emailAccountTagsUnassign(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const emailAccountTagsBulkUnassignRequest = createAsyncThunk<
  ResponseSuccess,
  EmailAccountTagsBulkUnassignPayload,
  { rejectValue: ResponseErrorWithHandled }
>('emailWarmup/emailAccountTagsBulkUnassign', async (args, thunkAPI) => {
  try {
    return await emailAccountTagsBulkUnassign(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const enableDisableDeleteSelectedEmailAccountWarmupRequest = createAsyncThunk<
  ResponseSuccess,
  { apiAction: ApiAction; emailAccountIds: number[] },
  { rejectValue: ResponseErrorWithHandled }
>(
  'emailWarmup/enableDisableSelectedEmailAccountWarmup',
  async ({ apiAction, emailAccountIds }, thunkAPI) => {
    try {
      return await enableDisableDeleteSelectedEmailAccountWarmup(
        apiAction,
        emailAccountIds,
      );
    } catch (e) {
      return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
    }
  },
);

export const enableDisableDeleteBulkEmailAccountWarmupRequest = createAsyncThunk<
  ResponseSuccess,
  {
    apiAction: ApiAction;
    deSelectedEmailAccountIds: number[];
    emailFilters: EmailAccountBulkActionFilters;
  },
  { rejectValue: ResponseErrorWithHandled }
>(
  'emailWarmup/enableDisableDeleteBulkEmailAccountWarmup',
  async ({ apiAction, deSelectedEmailAccountIds, emailFilters }, thunkAPI) => {
    try {
      return await enableDisableDeleteBulkEmailAccountWarmup(
        apiAction,
        deSelectedEmailAccountIds,
        emailFilters,
      );
    } catch (e) {
      return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
    }
  },
);

export const getEmailAccountsBulkActionStatusRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('emailWarmup/getEmailAccountsBulkActionStatus', async (_, thunkAPI) => {
  try {
    const response = await getEmailAccountsBulkActionStatus();
    return response;
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const refreshEmailAccountRequest = createAsyncThunk<
  ResponseSuccess,
  number,
  { rejectValue: ResponseErrorWithHandled }
>('emailWarmup/refreshEmailAccount', async (emailAccountId, thunkAPI) => {
  try {
    return await refreshEmailAccount(emailAccountId);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const saveEmailWarmupErrorMessageRequest = createAsyncThunk<
  ResponseSuccess,
  EmailWarmupErrorMessagePayload,
  { rejectValue: ResponseErrorWithHandled }
>('emailWarmup/saveEmailWarmupErrorMessage', async (payload, thunkAPI) => {
  try {
    return await saveEmailWarmupErrorMessage(payload);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const getEmailAccountWarmupReportStatsRequest = createAsyncThunk<
  ResponseSuccess,
  EmailWarmupReportFiltersPayload,
  { rejectValue: ResponseErrorWithHandled }
>('emailWarmup/getEmailAccountWarmupReportStats', async (payload, thunkAPI) => {
  try {
    return await getEmailAccountWarmupReportStats(payload);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const getEmailAccountWarmupReportDeliverabilityScoreRequest = createAsyncThunk<
  ResponseSuccess,
  EmailWarmupReportFiltersPayload,
  { rejectValue: ResponseErrorWithHandled }
>(
  'emailWarmup/getEmailAccountWarmupReportDeliverabilityScore',
  async (payload, thunkAPI) => {
    try {
      return await getEmailAccountWarmupReportDeliverabilityScore(payload);
    } catch (e) {
      return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
    }
  },
);

export const getEmailAccountWarmupReportESPBreakdownRequest = createAsyncThunk<
  ResponseSuccess,
  EmailWarmupReportFiltersPayload,
  { rejectValue: ResponseErrorWithHandled }
>(
  'emailWarmup/getEmailAccountWarmupReportESPBreakdown',
  async (payload, thunkAPI) => {
    try {
      return await getEmailAccountWarmupReportESPBreakdown(payload);
    } catch (e) {
      return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
    }
  },
);

export const getEmailAccountWarmupReportGraphRequest = createAsyncThunk<
  ResponseSuccess,
  EmailWarmupReportFiltersPayload,
  { rejectValue: ResponseErrorWithHandled }
>('emailWarmup/getEmailAccountWarmupReportGraph', async (payload, thunkAPI) => {
  try {
    return await getEmailAccountWarmupReportGraph(payload);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const getEmailAccountWarmupReportExportRequest = createAsyncThunk<
  ResponseSuccess,
  EmailWarmupReportFiltersPayload,
  { rejectValue: ResponseErrorWithHandled }
>(
  'emailWarmup/getEmailAccountWarmupReportExport',
  async (payload, thunkAPI) => {
    try {
      return await getEmailAccountWarmupReportExport(payload);
    } catch (e) {
      return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
    }
  },
);

export const emailAccountsWarmupExportRequest = createAsyncThunk<
  ResponseSuccess,
  EmailAccountsWarmupExportPayload,
  { rejectValue: ResponseErrorWithHandled }
>('emailWarmup/emailAccountsWarmupExport', async (payload, thunkAPI) => {
  try {
    return await emailAccountsWarmupExport(payload);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const getEmailAccountWarmupBulkRunningStoppedStatusRequest = createAsyncThunk<
  ResponseSuccess,
  { emailFilters: EmailAccountWarmupFilters; emailAccountIds: number[] },
  { rejectValue: ResponseErrorWithHandled }
>(
  'emailWarmup/getEmailAccountWarmupBulkRunningStoppedStatus',
  async ({ emailFilters, emailAccountIds }, thunkAPI) => {
    try {
      return await getEmailAccountWarmupBulkRunningStoppedStatus(
        emailFilters,
        emailAccountIds,
      );
    } catch (e) {
      return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
    }
  },
);

export const getEmailActivitySummaryRequest = createAsyncThunk<
  ResponseSuccess,
  EmailActivitySummaryPayload,
  { rejectValue: ResponseErrorWithHandled }
>('emailWarmup/getEmailActivitySummary', async (payload, thunkAPI) => {
  try {
    return await getEmailAccountActivitySummary(payload);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const getEmailActivityEventsRequest = createAsyncThunk<
  ResponseSuccess,
  EmailActivityEventsPayload,
  { rejectValue: ResponseErrorWithHandled }
>('emailWarmup/getEmailActivityEvents', async (payload, thunkAPI) => {
  try {
    return await getEmailAccountActivityEvents(payload);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const getWarmupActivityHistoryRequest = createAsyncThunk<
  ResponseSuccess,
  WarmupActivityHistoryPayload,
  { rejectValue: ResponseErrorWithHandled }
>('emailWarmup/getWarmupActivityHistory', async (payload, thunkAPI) => {
  try {
    return await getEmailAccountWarmupActivityHistory(payload);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});
